import { useEffect, useRef, useState } from 'react'
import './player-activity.styles.scss';
import { changeDocumentTitle } from '@/utils/useDocumentTitle';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter } from '@fortawesome/free-solid-svg-icons';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import DetailedActivityListTable from '@/components/SHARED/detailed-activity-list/detailed-activity-list-table.component';
import Modal from '@/components/modal/modal.component';
import ActivityFiltersV2 from '@/components/SHARED/detailed-activity-list/activity-filters-v2.component';
import TransactionView from '@/components/SHARED/transaction-view/transaction-view.component';
import { useSelector } from 'react-redux';
import { getActivityRecordsQuery } from '@/utils/getDataFromDb';
import { gameActivityComplete } from '@/components/PREVIEW/previewGameActivity';

const PlayerActivity = ({
  gameData,
  playerData,
  gameElements,
}) => {

  const screenSize = useSelector(state => state.contentData.screenSize);
  const [activityToDisplay, setActivityToDisplay] = useState([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [queryFilter, setQueryFilter] = useState([]);
  const [resetFilter, setResetFilter] = useState(false);
  const [noMore, setNoMore] = useState(false);
  const [moreLimit] = useState(10);
  const scrollRef = useRef(null);
  const activityRef = useRef({});
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!gameData?.gameName) return;
    changeDocumentTitle(`View Game Activity | ${gameData?.gameName}`);
  }, [gameData?.gameName]);

  useEffect(() => {
    if (!playerData || !playerData.playerId) return;
    const pageTitle = document.getElementById('page-title');
    if (pageTitle) {
      pageTitle.innerText = 'View Game Activity'
    }
    getActivityData();
  }, [playerData?.playerId])

  useEffect(() => {
    // console.log('scrolling')
    let scrollElement;
    if (screenSize === 'mobile') {
      scrollElement = document.querySelector('.detailed-activity-list-table');
    } else {
      scrollElement = document.querySelector('#scroll-to-top');
    }

    let timeoutId;
    if (scrollRef.current && scrollElement) {
      timeoutId = setTimeout(() => {
        scrollElement.scrollTo({
          'behavior': 'smooth',
          'top': scrollRef.current + 40
        });
        clearTimeout(timeoutId);
      }, 100);
    } else if (scrollElement) {
      scrollElement.scrollTo({
        'behavior': 'smooth',
        'top': 0
      });
    }

    return () => {
      scrollRef.current = null;
      if (timeoutId) clearTimeout(timeoutId);
    }
  }, [location])

  function getActivityData(next) {
    if (location.pathname.includes('/preview/player/')) {
      let playerActivity = Object.values(gameActivityComplete).filter(a => a.playerId === playerData.playerId);
      for (let query of queryFilter) {
        playerActivity = playerActivity.filter(a => a.key === query.value);
      }
      setActivityToDisplay(playerActivity);
      setNoMore(true);
      return;
    }
    const filters = queryFilter.some(q => q.key === 'playerId')
      ? queryFilter
      : [...queryFilter, { key: 'playerId', value: playerData.playerId }];
    const queryObject = {
      'gameId': gameData.gameId,
      'path': 'activityRecords',
      'filter': filters,
      'limit': moreLimit,
      'once': true,
      'callback': callback
    }
    if (next) {
      const len = Object.keys(activityRef.current).length
      queryObject.lastEntry = Object.values(activityRef.current)
        .sort((a, b) => a.ts < b.ts ? 1 : -1)[len - 1];
    }
    // Handle 'next' filter
    getActivityRecordsQuery(queryObject)

    function callback(data) {
      // console.log(data);
      if (next) {
        activityRef.current = {
          ...activityRef.current,
          ...data
        };
      } else {
        activityRef.current = { ...data };
      }
      if (Object.keys(data).length < moreLimit) {
        setNoMore(true);
      } else {
        setNoMore(false);
      }
      setActivityToDisplay(activityRef.current);
    }
  }

  function goToTransaction(path) {
    if (screenSize === 'mobile') {
      const el = document.querySelector('.detailed-activity-list-table');
      if (!el) return;
      scrollRef.current = el.scrollTop;
    } else {
      const el = document.querySelector('#scroll-to-top');
      if (!el) return;
      scrollRef.current = el.scrollTop;
    }
    navigate(path);
  }

  function nextPage() {
    getActivityData(true);
    const timeout = setTimeout(() => {
      if (screenSize === 'mobile') {
        scrollRef.current = document.querySelector('.detailed-activity-list-table').scrollTop;
      } else {
        scrollRef.current = document.querySelector('#scroll-to-top').scrollTop;
      }

    }, 200)
    clearTimeout(timeout);
  }

  return (
    <div className='game-activity-container'>

      <Routes>
        <Route path='' element={
          <div className='game-activity-content g-card'>
            <div className='player-title'>
              <ImageContainer src='/images/icons/list.png' alt='rewards icon' className='player-title-icon' />
              Your Activity
            </div>
            <hr />
            <div className='filter-row buttons'>
              <button
                className='g-button small-btn'
                onClick={() => setShowFilterModal(true)} >
                Filter
                <FontAwesomeIcon
                  className={
                    queryFilter
                      ?.filter(q => q.key !== 'type' && q.key !== 'playerId')
                      .length > 0 ? 'active' : 'inactive'}
                  icon={faFilter} />
              </button>
            </div>
            <div className="g-space-1"></div>
            <DetailedActivityListTable
              gameData={gameData}
              activityToDisplay={activityToDisplay}
              playerList={{ [playerData.playerId]: playerData }}
              gameElements={gameElements}
              manager={false}
              gameManagers={[]}
              goToTransaction={goToTransaction}
              hidePlayer={true} />
            <div className="g-space-1"></div>
            <div className='center-col'>
              <button
                className={`g-button small-btn ${noMore ? 'disabled' : ''}`}
                onClick={() => nextPage()}>
                Load More...
              </button>
            </div>
          </div>
        }></Route>
        <Route
          path=':transPath'
          element={
            <div className='transaction-container'>
              <div className='transaction-card g-card'>
                <TransactionView
                  activityToDisplay={activityToDisplay}
                  playerList={{
                    [playerData.playerId]: playerData
                  }}
                  gameElements={gameElements}
                  gameManagers={[]} />
              </div>
            </div>
          }
        ></Route>
      </Routes>
      <Modal
        show={showFilterModal}
        cancel={() => setShowFilterModal(false)}
        text="Filter Activity Records"
        closeButton={true}
        cls="narrow" >
        <div className='filter-modal-content'>
          <ActivityFiltersV2
            gameData={gameData}
            gameManagers={[]}
            playerList={{}}
            gameElements={gameElements}
            setQueryFilter={setQueryFilter}
            resetFilter={resetFilter}
            setResetFilter={setResetFilter}
          />
          <hr />
          <div className='buttons'>
            <button
              className='g-button med-btn'
              onClick={() => setResetFilter(true)}>
              Clear Filters
            </button>
            <button
              className='g-button med-btn primary'
              onClick={() => {
                setShowFilterModal(false);
                getActivityData(false)
              }}>
              Apply Filters
            </button>
          </div>
        </div>
      </Modal>

    </div>
  )
}

export default PlayerActivity